import { NO_POS } from '#core/lib/expr/ast.ts';
import { describeColumnType } from '#core/lib/expr/schema/types.ts';
import { useSession } from '#core/lib/expr/session/SessionContext.ts';
import { useEvalExpr } from '#core/lib/expr/session/useEvalExpr.ts';
import { useMaterialize } from '#core/lib/expr/session/useMaterialize.ts';
import { expectDataset } from '#core/lib/expr/value.ts';

import { withMention } from '#plugin-chat/components/tools/helpers/withMention.ts';
import {
  type TransformDatasetInput,
  TransformDatasetInputSchema,
  type TransformDatasetOutput,
} from '#plugin-chat/components/tools/transform-dataset/schema.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { summarizeDataset } from '#plugin-chat/lib/tool/dataset-summary.ts';
import { stageDatasetBinding } from '#plugin-chat/lib/tool/datasetBinding.ts';
import { DatasetPreview, ToolErrorMessage } from '#plugin-chat/lib/tool/message-components.tsx';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';

/**
 * TransformDatasetTool — s-expr-driven dataset transform.
 *
 * The LLM writes an s-expression — typically built around
 * `(transform <inputDataset> <verb> ...)` — that evaluates to a
 * `Dataset`. `execute` evaluates the expression (cheap — verb dispatch
 * appends plan nodes to the LazyDataset, no I/O) and **stages** the
 * result as an immutable replay recipe under `args.outputDataset` via
 * `stageDatasetBinding`; the `<Tool>` execute boundary commits it only
 * if this success envelope is actually returned, so a failed/errored
 * call never leaves or persists a binding. The recipe is persisted to
 * localStorage and replayed on session (re)creation, so a page reload /
 * thread switch restores prior datasets. Bindings are immutable:
 * reusing an `outputDataset` name throws `SessionRebindingError` at
 * commit (a recoverable tool error carrying a concretely-free suggested
 * name), keeping prior `(col …)` references stable. The LLM gets a schema-summary envelope back synchronously so
 * it can chain further tool calls without waiting for materialisation.
 *
 * `render` calls `useMaterializedExpr(args.expression)` to force the
 * dataset on demand for the visible chrome; sibling renders for the
 * same expression share TanStack's Suspense cache.
 *
 * No factory props, no per-call EvalContext assembly —
 * `<ChatSessionProvider>` populates `EvalDepsContext` once for the
 * whole conversation and every hook below reads from it.
 *
 * The full s-expression grammar (transform verbs, agg-context funcs,
 * column primitives) is documented once in the shared
 * `<ExprDslReference>` system-prompt block (see `AtlasInstructions`),
 * not re-embedded per tool.
 *
 * The strategic guidance dropped during the data-pipeline → s-expr
 * migration is restored on the `description` prop via a nested
 * `<ToolGuidance>` (the new `<Tool>` has no `instructions` channel):
 * discover-distinct-values-before-filtering, aggregate-never-sample,
 * the timeseries-from-a-ref-column idioms (unnest for raw points,
 * time-bucket for aggregated buckets), wide-shaping for charts, and the
 * join verb — the latter two folding back the salvageable guidance from
 * the removed `fetch-timeseries-datapoints` and `join-datasets` tools.
 * Recast against the live verb set; no old data-pipeline op syntax
 * survives (`expandTimeseries`/`daily()`/`avg` have no equivalent).
 */
export const TransformDatasetTool = withMention('transformDataset', function TransformDatasetToolImpl() {
  const evalExpr = useEvalExpr();
  const materialize = useMaterialize();
  // Session needed to stage the result under outputDataset. The
  // engine's evalExpr doesn't auto-bind — that's a tool-layer concern.
  const session = useSession();

  return (
    <Tool<TransformDatasetInput, TransformDatasetOutput>
      description={
        <ToolGuidance
          description="Run an s-expression transformation over a previously-bound dataset and record the result as a new immutable, persisted binding."
          tool={TransformDatasetTool}
          whenNotToUse={[
            'Fetching instance data from the knowledge graph (use `queryKnowledgeGraph`)',
            'Presenting results to the user (use `showDashboard`)',
          ]}
          whenToUse={[
            'Filtering, deriving, sorting, slicing, aggregating, joining, or reshaping a previously-bound dataset.',
            'Fetching/aggregating timeseries datapoints from a `ref<timeseries>` column produced by `queryKnowledgeGraph`.',
            'Discovering the value domain of a categorical column before filtering it — run `(aggregate (n (count)) :by [<col>])` to reveal the actual distinct values and frequencies. Prefer this over guessing a filter value that may return 0 matches with no indication the guess was wrong.',
            'Answering any "how many X" / sum / mean / min/max / distribution question — must go through an `(aggregate …)`, never inferred from a row sample. Prefer grouping without a pre-applied filter.',
            'Combining two bound datasets on a shared key (the `join` verb) before further processing.',
            'Preparing a dataset for `showDashboard`.',
          ]}
        >
          <p>
            The expression usually wraps a single <code>(transform &lt;inputDataset&gt; &lt;verb&gt; ...)</code> call
            but any s-expression returning a Dataset is accepted; verbs chain left-to-right, each consuming the prior
            result. Datasets are lazy by default; rows materialise on demand. Pick a <strong>unique</strong>{' '}
            <code>outputDataset</code> name: bindings are immutable, so reusing a name is an error. Bindings persist
            across page reloads and thread switches. The full verb grammar (filter, derive, select, limit, distinct,
            rename, sort, aggregate, join, unnest, time-bucket, pivot, fold, spread, unroll) is documented in the shared
            DSL reference — the guidance below is strategic only.
          </p>
          <example name="transform-then-dashboard">
            <p>User: "Total revenue per region for the wells I just queried."</p>
            <ol>
              <li>
                <code>transformDataset</code> — inputDataset <code>top-wells</code>, expression{' '}
                <code>(transform top-wells (aggregate (revenue (sum revenue)) :by [region]))</code>, outputDataset{' '}
                <code>revenue-by-region</code> → binds <code>revenue-by-region</code>
              </li>
              <li>
                <code>showDashboard</code> — expression{' '}
                <code>
                  (dashboard (cartesian (series :type bar :id rev :label "Revenue" :color blue :x (col revenue-by-region
                  region) :y (col revenue-by-region revenue)) :span [12 6]))
                </code>
              </li>
            </ol>
          </example>

          <h3>Discover distinct values before filtering</h3>
          <p>
            Before filtering on an unfamiliar categorical column, run an aggregate with a count to reveal the actual
            distinct values and their frequencies: <code>(transform wells (aggregate (n (count)) :by [region]))</code>.
            Filtering on a guessed value that does not exist returns 0 rows with no signal the guess was wrong.
          </p>

          <h3>Aggregate, never sample</h3>
          <p>
            Any "how many" / total / mean / min / max / distribution answer MUST come from an{' '}
            <code>(aggregate (&lt;out&gt; (&lt;fn&gt; &lt;col&gt;)) :by [&lt;cols&gt;])</code> — never inferred from a
            row sample. Available functions: <code>sum</code>, <code>mean</code> (there is no <code>avg</code>),{' '}
            <code>min</code>, <code>max</code>, <code>count</code> (zero-arg: <code>(count)</code>), <code>first</code>,{' '}
            <code>last</code>, <code>value</code>, <code>values</code>, <code>timestamps</code>. Omit <code>:by</code>{' '}
            for a global 1-row rollup (the canonical shape to feed a <code>showDashboard</code> <code>metric</code>).
          </p>

          <h3>Timeseries from a ref column</h3>
          <p>
            A <code>ref&lt;timeseries&gt;</code> column produced by <code>queryKnowledgeGraph</code> is itself a nested
            dataset of raw datapoints whose inner columns are <code>timestamp</code>/<code>value</code>/
            <code>isString</code>/<code>unit</code>.
          </p>
          <ul>
            <li>
              <strong>Raw, unaggregated points:</strong>{' '}
              <code>(transform ds (unnest [oilRef] :pick [timestamp value] :as [ts oil]))</code> — one row per
              (original-row, sample). To window raw points, <code>unnest</code> then <code>filter</code> on{' '}
              <code>ts</code>.
            </li>
            <li>
              <strong>Aggregated buckets (charts):</strong>{' '}
              <code>
                (transform ds (time-bucket [oilRef] :period day :window ["2026-01-01" "2026-02-01"] :agg [mean]))
              </code>{' '}
              — <code>:period</code> ∈ minute/hour/day/week/month/quarter/year; <code>:window</code> is a half-open
              ISO-8601 <code>[start end)</code> 2-tuple; <code>:agg</code> accepts bare fns <code>[min max mean]</code>{' '}
              or <code>(out (fn))</code> pairs. Use <code>time-bucket</code> only for aggregated buckets, never to
              retrieve raw samples.
            </li>
            <li>
              <strong>Latest scalar per row:</strong>{' '}
              <code>(transform ds (aggregate (latest (value)) :by [externalId]))</code> — <code>(value)</code> is the
              latest value of the per-cell timeseries. There is no per-row scalar <code>derive</code> for a window-mean;
              use <code>time-bucket</code> or <code>unnest</code> + <code>aggregate</code> instead.
            </li>
          </ul>

          <h3>Charting timeseries: shape it wide first</h3>
          <p>
            <code>showDashboard</code> binds one column per chart series. After <code>time-bucket</code> you have one
            bucketed column per metric (1×M — chart directly). For N entities × 1 metric, the data is{' '}
            <strong>long</strong> (one row per (timestamp, entity)); reshape to <strong>wide</strong> with{' '}
            <code>(… (pivot …))</code> so each entity becomes its own column, then point each{' '}
            <code>(series … :y (col &lt;ds&gt; &lt;entityCol&gt;))</code> at a distinct column — two series cannot share
            one column. When the consumer is a time-axis chart, produce one row per timestamp plus one numeric column
            per series and pass <code>(col &lt;ds&gt; timestamp)</code> as the series <code>:x</code>.
          </p>

          <h3>Joining two datasets</h3>
          <p>
            <code>(transform &lt;left&gt; (join &lt;right&gt; :on [&lt;keys&gt;] :type left))</code>. <code>:type</code>{' '}
            ∈ <code>inner</code> (default) | <code>left</code> | <code>right</code> | <code>full</code>.{' '}
            <code>:on</code> entries are a bare symbol (same column name both sides), <code>[outer inner]</code>{' '}
            (different names), or <code>(col &lt;binding&gt; &lt;column&gt; [&lt;subpath&gt;…])</code> (struct sub-path
            / ref-identity key). A non-key column present on both sides throws — rename one side first; there is no
            auto-suffixing. For any further filter/derive/sort/aggregate on the joined result, chain another{' '}
            <code>transformDataset</code> on this tool's <code>outputDataset</code>.
          </p>
        </ToolGuidance>
      }
      display={{
        running: 'Transforming dataset...',
        progress: 'processing data',
        complete: { label: 'Transform complete', semantic: 'data-transform' },
        error: (props) => `Dataset transform error: ${props.result && 'error' in props.result && props.result?.error}`,
      }}
      execute={async (args: TransformDatasetInput): Promise<TransformDatasetOutput> => {
        // Eval is cheap — verb dispatch appends plan nodes to the
        // LazyDataset chain. No materialisation here; the schema is
        // fully resolved so the LLM can chain subsequent tool calls.
        const value = await evalExpr(args.expression);
        const ds = expectDataset(value, 'transformDataset result', NO_POS);
        const mat = await materialize(ds);
        // Stage an immutable replay recipe; the <Tool> boundary commits
        // it as the final step iff this success envelope is returned, so
        // a failed/errored call never leaves or persists a binding. A
        // duplicate name throws SessionRebindingError at commit; the
        // boundary routes it to the structured error envelope.
        return stageDatasetBinding(
          {
            success: true,
            state: mat.state,
            inputDataset: args.inputDataset,
            outputDataset: args.outputDataset,
            schema: Array.from(ds.schema.entries()).map(([name, type]) => ({
              name,
              kind: describeColumnType(type),
            })),
            summary: summarizeDataset(mat),
          },
          session,
          args.outputDataset,
          args.expression,
          ds,
        );
      }}
      message={{
        call: ({ args }) => (
          <p>
            Transforming <code>{args.inputDataset}</code> → <code>{args.outputDataset}</code>:{' '}
            <code>{args.expression}</code>
          </p>
        ),
        result: ({ output }) => (
          <>
            <p>
              Transformed <code>{output.inputDataset}</code> into <code>{output.outputDataset}</code> ({output.state}).
              Columns: [{output.schema.map((c) => `${c.name}:${c.kind}`).join(', ')}].{' '}
              <code>{output.outputDataset}</code> is an immutable binding referenceable by downstream{' '}
              <code>transformDataset</code>/<code>showDashboard</code> calls — do not reuse this name.
            </p>
            <DatasetPreview summary={output.summary} />
          </>
        ),
        error: ({ args, envelope, toolName }) => (
          <ToolErrorMessage
            envelope={envelope}
            recovery={
              <p>
                Input <code>{args.inputDataset}</code>, expression <code>{args.expression}</code>. Confirm the input
                dataset is staged and the verb/columns exist, then retry.
              </p>
            }
            toolName={toolName}
          />
        ),
      }}
      name="transformDataset"
      parameters={TransformDatasetInputSchema}
    />
  );
});
